<template>
  <el-skeleton animated test-id="main">
    <template #template>
      <!-- Top section -->
      <div class="mt-14">
        <div
          class="px-8 py-10 flex flex-col md:flex-row md:items-center justify-between gap-y-10"
        >
          <div
            class="flex flex-col md:flex-row items-center md:items-start gap-x-5"
          >
            <!-- Currency Icons Placeholder -->
            <el-skeleton-item
              animated
              class="h-7 w-7 lg:h-7 lg:w-7 mb-3 lg:mb-0"
            ></el-skeleton-item>
            <!-- Text Placeholder -->
            <div class="flex flex-col items-center md:items-start">
              <el-skeleton-item
                variant="text"
                animated
                class="w-24 mb-3 md:mb-0"
              ></el-skeleton-item>
              <el-skeleton-item
                variant="text"
                animated
                class="w-48 mt-1 h-5"
              ></el-skeleton-item>
            </div>
          </div>
          <!-- right -->
          <div class="relative">
            <div
              class="right flex items-center justify-center lg:justify-start gap-x-4 z-10"
            >
              <!-- Deposit Button Placeholder -->
              <el-skeleton-item
                variant="text"
                animated
                class="w-24 h-10"
              ></el-skeleton-item>
              <!-- Withdraw Button Placeholder -->
              <el-skeleton-item
                variant="text"
                animated
                class="w-24 h-10"
              ></el-skeleton-item>
            </div>
          </div>
        </div>
      </div>
      <!-- Request new account skeleton -->
      <div
        v-if="tenantPermission('multiple-currency-deposit-access')"
        class="mt-5"
      >
        <el-skeleton animated>
          <template #template>
            <div
              class="flex md:flex-row flex-col justify-between items-center gap-x-4"
            >
              <div
                v-for="(_, index) in [1, 2, 3, 4, 5]"
                :key="index"
                class="flex items-center mb-3 w-full"
              >
                <el-skeleton-item
                  variant="rect"
                  animated
                  class="w-full h-28"
                ></el-skeleton-item>
              </div>
            </div>
          </template>
        </el-skeleton>
      </div>
      <!-- Make transfer -->
      <div class="mt-5">
        <el-skeleton animated>
          <template #template>
            <div
              class="flex md:flex-row flex-col justify-between items-center gap-x-4"
            >
              <div
                v-for="(_, index) in [1, 2, 3, 4]"
                :key="index"
                class="flex items-center mb-3 w-full"
              >
                <el-skeleton-item
                  variant="rect"
                  animated
                  class="w-full h-32"
                ></el-skeleton-item>
              </div>
            </div>
          </template>
        </el-skeleton>
      </div>
      <div
        v-if="tenantPermission('display-dashboard-exchange-rates')"
        class="md:grid grid-cols-1 grid-cols-12 gap-x-8 mt-8"
      >
        <!-- Exchange Rates -->
        <div class="md:col-span-6 mb-5 border-main py-6 rounded-lg md:h-full">
          <el-skeleton animated>
            <template #template>
              <!-- Header Skeleton -->
              <div class="lg:px-10 px-3">
                <div class="flex justify-between pb-5">
                  <!-- Title and Subtitle -->
                  <div>
                    <el-skeleton-item
                      variant="text"
                      animated
                      class="w-28 mb-2"
                    ></el-skeleton-item>
                    <el-skeleton-item
                      variant="text"
                      animated
                    ></el-skeleton-item>
                  </div>
                  <!-- "See More" Button -->
                  <el-skeleton-item
                    variant="text"
                    animated
                    class="rounded-lg w-16 lg:w-28 h-10"
                  ></el-skeleton-item>
                </div>
              </div>
              <!-- Content Skeleton -->
              <div class="lg:px-10 px-3">
                <div class="flex justify-between items-center">
                  <!-- Currency Pair Skeleton -->
                  <div class="flex items-center gap-x-2.5">
                    <el-skeleton-item
                      variant="rect"
                      animated
                      class="w-6 h-6"
                    ></el-skeleton-item>
                    <el-skeleton-item
                      variant="rect"
                      animated
                      class="w-6 h-6"
                    ></el-skeleton-item>
                    <el-skeleton-item
                      variant="text"
                      animated
                      class="w-20"
                    ></el-skeleton-item>
                    <el-skeleton-item
                      variant="text"
                      animated
                      class="w-4 h-4"
                    ></el-skeleton-item>
                  </div>
                  <!-- Rate and Percentage Change Skeleton -->
                  <el-skeleton-item
                    variant="rect"
                    animated
                    class="w-28"
                  ></el-skeleton-item>
                </div>
              </div>
              <!-- Chart Skeleton -->
              <div class="mt-5 lg:px-10 px-3">
                <el-skeleton-item
                  animated
                  class="md:h-1/2 h-40"
                ></el-skeleton-item>
                <!-- Buttons Skeleton -->
                <div class="flex items-center gap-x-4 mt-4">
                  <el-skeleton-item
                    variant="rect"
                    animated
                    class="w-1/2 h-10 rounded-lg"
                  ></el-skeleton-item>
                  <el-skeleton-item
                    variant="rect"
                    animated
                    class="w-1/2 h-10 rounded-lg"
                  ></el-skeleton-item>
                </div>
              </div>
            </template>
          </el-skeleton>
        </div>
        <!-- Transaction List -->
        <div class="md:col-span-6">
          <el-skeleton
            class="rounded-lg bg-surface border-main br-root-2 py-6 md:h-full"
            animated
          >
            <template #template>
              <!-- Header Skeleton -->
              <div class="border-b br-root-2">
                <div class="flex justify-between lg:px-10 px-3 pb-5">
                  <!-- Title and Subtitle -->
                  <div>
                    <el-skeleton-item
                      variant="text"
                      animated
                      class="w-28 mb-2"
                    ></el-skeleton-item>
                    <el-skeleton-item
                      variant="text"
                      animated
                    ></el-skeleton-item>
                  </div>
                  <!-- "See All" Button -->
                  <el-skeleton-item
                    variant="text"
                    animated
                    class="rounded-lg w-16 lg:w-28 h-10"
                  ></el-skeleton-item>
                </div>
              </div>
              <!-- Content Skeleton -->
              <div>
                <!-- Transaction Skeleton -->
                <div
                  v-for="(_, index) in [1, 2, 3, 4]"
                  :key="index"
                  class="md:px-7"
                >
                  <div class="border-b br-root-2 cursor-pointer">
                    <div class="flex justify-between py-4 px-5">
                      <!-- Icon and Transaction Details -->
                      <div class="flex items-center gap-x-3 md:gap-x-6">
                        <el-skeleton-item
                          animated
                          variant="rect"
                          class="w-10 h-10"
                        ></el-skeleton-item>
                        <div class="flex flex-col gap-y-2">
                          <el-skeleton-item
                            animated
                            variant="text"
                            class="w-20"
                          ></el-skeleton-item>
                          <el-skeleton-item
                            animated
                            variant="text"
                            class="w-16"
                          ></el-skeleton-item>
                        </div>
                      </div>
                      <!-- Amount and Status -->
                      <div class="flex items-center md:gap-x-8 gap-x-2">
                        <div class="flex flex-col gap-y-2">
                          <el-skeleton-item
                            animated
                            variant="text"
                            class="w-16"
                          ></el-skeleton-item>
                          <el-skeleton-item
                            animated
                            variant="text"
                            class="w-16"
                          ></el-skeleton-item>
                        </div>
                        <el-skeleton-item
                          animated
                          variant="rect"
                          class="w-4 h-4"
                        ></el-skeleton-item>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </el-skeleton>
        </div>
      </div>
    </template>
  </el-skeleton>
</template>
<script>
import tenant from '@/mixins/tenant.js'
export default {
  mixins: [tenant]
}
</script>
